
const STORAGE_KEY = "cestaconnect.sso.tokens";

/**
 * Parse the one-time SSO exchange code from the URL hash.
 *
 * Example:
 * #code=abc123&tenantKey=acme-kc
 */
export function parseHashExchangeCode(hash) {
  const raw = hash.startsWith("#")
    ? hash.slice(1)
    : hash;

  if (!raw) {
    return null;
  }

  return new URLSearchParams(raw).get("code");
}

/**
 * Get SSO base URL.
 */
export function ssoBaseUrl() {
  return (
    process.env.REACT_APP_SSO_BASE_URL?.replace(/\/$/, "") ||
    "http://localhost:8080"
  );
}

/**
 * Get SSO login URL.
 */
export function ssoLoginUrl() {
  const tenantKey =
    process.env.REACT_APP_SSO_TENANT_KEY ||
    "acme-kc";

  return `${ssoBaseUrl()}/api/v1/auth/login/${tenantKey}`;
}

/**
 * Exchange the one-time code for SSO tokens.
 */
export async function exchangeCodeForTokens(code) {
  const response = await fetch(
    `${ssoBaseUrl()}/api/v1/auth/exchange`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ code }),
    }
  );

  if (!response.ok) {
    throw new Error(
      `Code exchange failed (${response.status})`
    );
  }

  const body = await response.json();

  return {
    accessToken: body.access_token,
    refreshToken: body.refresh_token ?? null,
    tokenType: body.token_type ?? null,
    expiresIn: body.expires_in ?? null,
    refreshExpiresIn:
      body.refresh_expires_in ?? null,
    tenantKey: body.tenantKey ?? null,
    jti: body.jti ?? null,

    expiresAt:
      body.expires_in != null
        ? Date.now() + body.expires_in * 1000
        : null,
  };
}

/**
 * Save SSO tokens in session storage and local storage for cross-tab synchronization.
 */
export function saveTokens(tokens) {
  const json = JSON.stringify(tokens);
  sessionStorage.setItem(STORAGE_KEY, json);
  localStorage.setItem(STORAGE_KEY, json);
}

/**
 * Load SSO tokens.
 */
export function loadTokens() {
  const raw =
    sessionStorage.getItem(STORAGE_KEY) ||
    localStorage.getItem(STORAGE_KEY);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

/**
 * Clear SSO tokens.
 */
export function clearTokens() {
  sessionStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Logout from SSO.
 */
export async function logoutFromSso(tokens) {
  const postLogoutRedirectUri =
    `${window.location.origin}/`;

  const response = await fetch(
    `${ssoBaseUrl()}/api/v1/auth/logout`,
    {
      method: "POST",

      headers: {
        Authorization:
          `Bearer ${tokens.accessToken}`,
        "Content-Type": "application/json",
      },

      body: JSON.stringify({
        refreshToken: tokens.refreshToken,
        federatedLogout: true,
        postLogoutRedirectUri,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(
      `Logout failed (${response.status})`
    );
  }

  const body = await response.json();

  clearTokens();

  return body.federated_logout_url || null;
}

/**
 * Shorten JWT for displaying in UI.
 */
export function truncateJwt(token) {
  if (token.length <= 48) {
    return token;
  }

  return `${token.slice(0, 24)}…${token.slice(-16)}`;
}

